import { useEffect, useState } from "react";
import { db } from "@/lib/db";
import { useToastStore } from "@/stores/toastStore";
import { FolderOpen, Download, X } from "lucide-react";
import type { Document } from "@/types";

interface ContextualActionBarProps {
  selectedIds: string[];
  onClear: () => void;
  onAction: () => void;
}

export function ContextualActionBar({ selectedIds, onClear, onAction }: ContextualActionBarProps) {
  const [showCollectionMenu, setShowCollectionMenu] = useState(false);
  const { addToast } = useToastStore();

  const handleAddToCollection = async (collectionId: string) => {
    for (const docId of selectedIds) {
      const exists = await db.collectionItems.where({ collectionId, documentId: docId }).first();
      if (!exists) {
        await db.collectionItems.add({
          id: crypto.randomUUID(),
          collectionId,
          documentId: docId,
          createdAt: new Date().toISOString(),
        });
      }
    }
    addToast({ message: `Added ${selectedIds.length} to collection`, type: "success" });
    onAction();
    setShowCollectionMenu(false);
  };

  const handleExport = async () => {
    const docs = await db.documents.bulkGet(selectedIds);
    const validDocs = docs.filter((d): d is Document => !!d && !!d.opfsPath);
    const JSZip = (await import("jszip")).default;
    const zip = new JSZip();
    for (const doc of validDocs) {
      const { readBlob } = await import("@/lib/opfs");
      const blob = await readBlob(doc.opfsPath!);
      if (blob) zip.file(doc.canonicalName, blob);
    }
    const content = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(content);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attachdrive-export-${new Date().toISOString().slice(0, 10)}.zip`;
    a.click();
    URL.revokeObjectURL(url);
    addToast({ message: `Exported ${validDocs.length} files`, type: "success" });
    onAction();
  };

  if (selectedIds.length === 0) return null;

  return (
    <div className="sticky top-14 z-20 flex items-center gap-3 rounded-xl border border-border-default bg-surface-0 px-4 py-2.5 shadow-md dark:border-dark-border-default dark:bg-dark-surface-1">
      <span className="text-sm font-semibold text-text-primary dark:text-dark-text-primary">
        {selectedIds.length} selected
      </span>

      <div className="relative">
        <button
          onClick={() => setShowCollectionMenu((s) => !s)}
          className="flex items-center gap-1.5 rounded-lg bg-accent px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-accent-600"
        >
          <FolderOpen size={13} />
          Add to Collection
        </button>
        {showCollectionMenu && (
          <div className="absolute left-0 top-full mt-1.5 w-52 rounded-xl border border-border-default bg-surface-0 py-1 shadow-xl dark:border-dark-border-default dark:bg-dark-surface-1">
            <CollectionMenu onSelect={handleAddToCollection} onClose={() => setShowCollectionMenu(false)} />
          </div>
        )}
      </div>

      <button
        onClick={handleExport}
        className="flex items-center gap-1.5 rounded-lg border border-border-strong bg-surface-0 px-3 py-1.5 text-xs font-medium text-text-secondary transition-colors hover:bg-surface-2 dark:border-dark-border-strong dark:bg-dark-surface-1 dark:text-dark-text-secondary dark:hover:bg-dark-surface-2"
      >
        <Download size={13} />
        Export ZIP
      </button>

      <button
        onClick={onClear}
        className="ml-auto flex items-center gap-1 text-xs font-medium text-text-muted hover:text-text-secondary transition-colors dark:text-dark-text-muted dark:hover:text-dark-text-secondary"
      >
        <X size={13} />
        Clear
      </button>
    </div>
  );
}

function CollectionMenu({ onSelect, onClose }: { onSelect: (id: string) => void; onClose: () => void }) {
  const [collections, setCollections] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    db.collections.toArray().then((cols) => setCollections(cols));
  }, []);

  if (collections.length === 0) {
    return <div className="px-4 py-2.5 text-xs text-text-muted">No collections yet</div>;
  }

  return (
    <>
      {collections.map((col) => (
        <button
          key={col.id}
          onClick={() => { onSelect(col.id); onClose(); }}
          className="block w-full px-4 py-2 text-left text-sm text-text-secondary transition-colors hover:bg-surface-1 dark:text-dark-text-secondary dark:hover:bg-dark-surface-2"
        >
          {col.name}
        </button>
      ))}
    </>
  );
}
